import { LogManager } from "@mcleod/core";
import { Panel } from "@mcleod/components";
import { CapturedImage } from "./CapturedImage";
import { ImageBundle } from "./ImageBundle";
import { ImageBundleType } from "./ImageBundleType";
import { ImageBundleCaptureStyles } from "./ImageBundleCaptureStyles";

const log = LogManager.getLogger("capture.ImageBundleCapture");

/**
 * Callback type for persisting captured images.
 * Provided by the caller to define how images are submitted/uploaded.
 */
export type ImagePersistenceCallback = (images: CapturedImage[], bundleType: ImageBundleType) => Promise<void>;

/**
 * Props for ImageBundleCapture component.
 */
export interface ImageBundleCaptureProps {
    /** Initial bundle types to create. If not provided, no bundles are created initially. */
    initialBundleTypes?: ImageBundleType[];
    /** Callback for persisting/uploading images */
    onPersist?: ImagePersistenceCallback;
    /** Callback when any bundle's images change */
    onImagesChanged?: (allImages: Map<ImageBundleType, CapturedImage[]>) => void;
}

/**
 * Main container component for image capture functionality.
 * Manages one or more ImageBundle components and coordinates image capture/persistence.
 *
 * Usage:
 * ```typescript
 * const capture = new ImageBundleCapture({
 *     initialBundleTypes: [ImageBundleType.FREIGHT_PHOTOS],
 *     onPersist: async (images, bundleType) => {
 *         // Upload images to server
 *         await api.uploadImages(images);
 *     }
 * });
 * ```
 */
export class ImageBundleCapture extends Panel {
    private _bundles: Map<ImageBundleType, ImageBundle> = new Map();
    private _onPersist: ImagePersistenceCallback;
    private _onImagesChanged: (allImages: Map<ImageBundleType, CapturedImage[]>) => void;

    constructor(props?: Partial<ImageBundleCaptureProps>) {
        super({}, false);

        this._onPersist = props?.onPersist;
        this._onImagesChanged = props?.onImagesChanged;

        this.setProps({
            fillRow: true,
            fillHeight: true,
            padding: 16,
            scrollY: true
        });

        this._element.classList.add(ImageBundleCaptureStyles.captureContainer);

        // Create initial bundles if specified
        if (props?.initialBundleTypes) {
            for (const bundleType of props.initialBundleTypes) {
                this.addBundle(bundleType);
            }
        }
    }

    /**
     * Adds a new image bundle of the specified type.
     * @param bundleType The type of bundle to create
     * @param title Optional custom title for the bundle
     * @returns The created ImageBundle, or undefined if bundle type already exists
     */
    public addBundle(bundleType: ImageBundleType, title?: string): ImageBundle | undefined {
        if (this._bundles.has(bundleType)) {
            log.info(`Bundle of type ${bundleType} already exists`);
            return this._bundles.get(bundleType);
        }

        const bundle = new ImageBundle({
            bundleType,
            title,
            onImagesChanged: (images, type) => this.handleBundleImagesChanged(images, type)
        });

        this._bundles.set(bundleType, bundle);
        this.add(bundle);

        return bundle;
    }

    /**
     * Removes a bundle of the specified type.
     * @param bundleType The type of bundle to remove
     * @returns True if the bundle was removed, false if it didn't exist
     */
    public removeBundle(bundleType: ImageBundleType): boolean {
        const bundle = this._bundles.get(bundleType);
        if (!bundle) {
            return false;
        }

        this.remove(bundle);
        this._bundles.delete(bundleType);
        this.notifyAllImagesChanged();

        return true;
    }

    /**
     * Gets a bundle by type.
     */
    public getBundle(bundleType: ImageBundleType): ImageBundle | undefined {
        return this._bundles.get(bundleType);
    }

    /**
     * Gets all bundles.
     */
    public getAllBundles(): ImageBundle[] {
        return Array.from(this._bundles.values());
    }

    /**
     * Gets all captured images across all bundles.
     */
    public getAllImages(): Map<ImageBundleType, CapturedImage[]> {
        const allImages = new Map<ImageBundleType, CapturedImage[]>();
        for (const [type, bundle] of this._bundles) {
            allImages.set(type, bundle.images);
        }
        return allImages;
    }

    /**
     * Gets the total count of captured images across all bundles.
     */
    public getTotalImageCount(): number {
        let count = 0;
        for (const bundle of this._bundles.values()) {
            count += bundle.imageCount;
        }
        return count;
    }

    /**
     * Checks if there are any captured images.
     */
    public hasImages(): boolean {
        return this.getTotalImageCount() > 0;
    }

    /**
     * Clears all images from all bundles.
     */
    public clearAllImages(): void {
        for (const bundle of this._bundles.values()) {
            bundle.clearImages();
        }
        this.notifyAllImagesChanged();
    }

    /**
     * Sets the persistence callback.
     */
    set onPersist(callback: ImagePersistenceCallback) {
        this._onPersist = callback;
    }

    /**
     * Sets the images changed callback.
     */
    set onImagesChanged(callback: (allImages: Map<ImageBundleType, CapturedImage[]>) => void) {
        this._onImagesChanged = callback;
    }

    /**
     * Persists all images using the provided persistence callback.
     * @returns Promise that resolves when persistence is complete
     * @throws Error if no persistence callback is set
     */
    public async persistAllImages(): Promise<void> {
        if (!this._onPersist) {
            throw new Error("No persistence callback set. Provide onPersist in props or set it directly.");
        }

        const persistPromises: Promise<void>[] = [];

        for (const [bundleType, bundle] of this._bundles) {
            const images = bundle.images;
            if (images.length > 0) {
                persistPromises.push(this._onPersist(images, bundleType));
            }
        }

        await Promise.all(persistPromises);
    }

    /**
     * Persists images for a specific bundle type.
     * @param bundleType The bundle type to persist
     * @returns Promise that resolves when persistence is complete
     * @throws Error if no persistence callback is set or bundle doesn't exist
     */
    public async persistBundleImages(bundleType: ImageBundleType): Promise<void> {
        if (!this._onPersist) {
            throw new Error("No persistence callback set. Provide onPersist in props or set it directly.");
        }

        const bundle = this._bundles.get(bundleType);
        if (!bundle) {
            throw new Error(`Bundle of type ${bundleType} does not exist`);
        }

        const images = bundle.images;
        if (images.length > 0) {
            await this._onPersist(images, bundleType);
        }
    }

    /**
     * Handles when a bundle's images change.
     */
    private handleBundleImagesChanged(images: CapturedImage[], bundleType: ImageBundleType): void {
        this.notifyAllImagesChanged();
    }

    /**
     * Notifies the images changed callback with all current images.
     */
    private notifyAllImagesChanged(): void {
        if (this._onImagesChanged) {
            this._onImagesChanged(this.getAllImages());
        }
    }
}



